import React, { useState, useEffect, useRef } from 'react';
import { FiX, FiSearch, FiUser, FiUsers } from 'react-icons/fi';
import chatService from '../../../services/chatService';
import Avatar from '../Shared/Avatar';
import '../../Projects/ProjectComposer.css';

/**
 * NewMessageModal Component
 * Modal for searching and selecting users/teams to start a new conversation
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether modal is open
 * @param {function} props.onClose - Handler to close modal
 * @param {function} props.onConversationCreated - Handler called when conversation is created
 */
const NewMessageModal = ({ isOpen, onClose, onConversationCreated }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [usersPage, setUsersPage] = useState(1);
  const [teamsPage, setTeamsPage] = useState(1);
  const [hasMoreUsers, setHasMoreUsers] = useState(true);
  const [hasMoreTeams, setHasMoreTeams] = useState(true);
  const [isCreating, setIsCreating] = useState(false);

  const searchInputRef = useRef(null);
  const modalRef = useRef(null);
  const contentRef = useRef(null); // Ref for scroll container

  // Reset state and fetch initial data when modal opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => searchInputRef.current?.focus(), 100);

      // Reset state
      setSearchQuery('');
      setUsers([]);
      setTeams([]);
      setUsersPage(1);
      setTeamsPage(1);
      setHasMoreUsers(true);
      setHasMoreTeams(true);

      // Fetch initial data (empty query)
      fetchInitialData('');
    }
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const fetchInitialData = async (query) => {
    setIsLoading(true);
    try {
      // Parallel fetch for users and teams page 1
      const [usersRes, teamsRes] = await Promise.all([
        chatService.searchUsers(query, 1),
        chatService.searchTeams(query, 1)
      ]);

      // Parse Users
      const usersData = usersRes.data?.data?.users || usersRes.data?.users || [];
      const usersMeta = usersRes.data?.data?.meta || usersRes.data?.meta || {};
      setUsers(usersData);
      setUsersPage(1);
      // Check if current page < last page
      setHasMoreUsers(usersMeta.current_page < usersMeta.last_page);

      // Parse Teams
      const teamsData = teamsRes.data?.data?.teams || teamsRes.data?.teams || [];
      const teamsMeta = teamsRes.data?.data?.meta || teamsRes.data?.meta || {};
      setTeams(teamsData);
      setTeamsPage(1);
      setHasMoreTeams(teamsMeta.current_page < teamsMeta.last_page);

    } catch (error) {
      console.error('Failed to fetch initial data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadMore = async () => {
    if (isLoading) return;

    // Determine what to load
    const loadUsers = hasMoreUsers;
    const loadTeams = hasMoreTeams;

    if (!loadUsers && !loadTeams) return;

    setIsLoading(true);
    try {
      const promises = [];
      const types = [];

      if (loadUsers) {
        promises.push(chatService.searchUsers(searchQuery, usersPage + 1));
        types.push('users');
      }
      if (loadTeams) {
        promises.push(chatService.searchTeams(searchQuery, teamsPage + 1));
        types.push('teams');
      }

      const results = await Promise.all(promises);

      results.forEach((res, index) => {
        const type = types[index];
        if (type === 'users') {
          const newUsers = res.data?.data?.users || res.data?.users || [];
          const meta = res.data?.data?.meta || res.data?.meta || {};

          setUsers(prev => [...prev, ...newUsers]);
          setUsersPage(prev => prev + 1);
          setHasMoreUsers(meta.current_page < meta.last_page);
        } else if (type === 'teams') {
          const newTeams = res.data?.data?.teams || res.data?.teams || [];
          const meta = res.data?.data?.meta || res.data?.meta || {};

          setTeams(prev => [...prev, ...newTeams]);
          setTeamsPage(prev => prev + 1);
          setHasMoreTeams(meta.current_page < meta.last_page);
        }
      });

    } catch (error) {
      console.error('Failed to load more data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Search effect (debounced)
  useEffect(() => {
    if (!isOpen) return;

    // Initial fetch is handled by mount effect.
    // This effect handles user typing.
    // We need to skip this effect on initial mount if query is empty, OR we can let it run.
    // To avoid double fetch on mount, checking if query changed or if it's not the initial mount would be good.
    // But since we reset state on mount, let's use a flag or just simplistic approach:
    // If we call fetchInitialData('') on mount, and this effect runs on mount with '', it's a double fetch.
    // However, the mount effect sets query to ''.

    // Better logic: Remove fetchInitialData from mount effect, and let this effect handle it?
    // User might open modal with previous state if we didn't reset. We DO reset.
    // Let's rely on this effect for all fetching including initial one?
    // But we need to reset pagination.

    // Combined approach:
    // Mount effect resets state and sets searchQuery to ''. This triggers this effect.
    // So we DON'T need explicit fetchInitialData('') in mount effect if we have this effect.
    // But we need to ensure pagination is reset properly.

  }, [searchQuery]); // Removed full logic here to avoid conflict with below

  // Real Search Effect
  useEffect(() => {
    if (!isOpen) return;

    const handler = setTimeout(() => {
      // Reset pagination for new search
      setUsersPage(1);
      setTeamsPage(1);
      fetchInitialData(searchQuery);
    }, 300);

    return () => clearTimeout(handler);
  }, [searchQuery, isOpen]);


  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    // Load more when within 50px of bottom
    if (scrollHeight - scrollTop <= clientHeight + 50) {
      loadMore();
    }
  };

  const handleStartDirectChat = async (user) => {
    if (isCreating) return;
    setIsCreating(true);
    try {
      const response = await chatService.createConversation({
        type: 'direct',
        user_id: user.id
      });
      const conversation = response.data?.conversation || response.data;
      onConversationCreated(conversation);
      onClose();
    } catch (error) {
      console.error('Failed to create direct conversation:', error);
      // alert('Failed to start conversation'); // Removed alert for cleaner UI
    } finally {
      setIsCreating(false);
    }
  };

  const handleStartTeamChat = async (team) => {
    if (isCreating) return;
    setIsCreating(true);
    try {
      const response = await chatService.createConversation({
        type: 'team',
        team_id: team.id
      });
      const conversation = response.data?.conversation || response.data;
      onConversationCreated(conversation);
      onClose();
    } catch (error) {
      console.error('Failed to create team conversation:', error);
    } finally {
      setIsCreating(false);
    }
  };

  if (!isOpen) return null;

  const hasResults = users.length > 0 || teams.length > 0;

  return (
    <div className="project-composer-overlay" onClick={onClose}>
      <div
        className="project-composer-modal"
        ref={modalRef}
        onClick={(e) => e.stopPropagation()}
        style={{ width: '600px', maxHeight: '80vh' }}
      >
        {/* Header */}
        <div className="project-composer-header">
          <h2 className="project-composer-title">New Message</h2>
          <button
            className="project-composer-close"
            onClick={onClose}
            aria-label="Close"
            disabled={isCreating}
          >
            <FiX />
          </button>
        </div>

        <div className="project-composer-body" style={{ display: 'flex', flexDirection: 'column', gap: '20px', padding: '28px 32px' }}>

          {/* Search Input */}
          <div className="project-form-group" style={{ marginBottom: 0 }}>
            <div className="project-form-input" style={{ display: 'flex', alignItems: 'center', padding: '10px 14px', gap: '10px', background: 'rgba(242, 242, 242, 0.45)', border: '1px solid #AEAEAE', borderRadius: '10px' }}>
              <FiSearch style={{ color: '#606060' }} size={16} />
              <input className="ui-font-14"
                ref={searchInputRef}
                type="text"
                placeholder="Search people or teams..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                disabled={isCreating}
                style={{ border: 'none', background: 'transparent', outline: 'none', width: '100%', fontFamily: 'Montserrat, sans-serif', fontWeight: 600, color: '#202020' }}
              />
            </div>
          </div>

          {/* Results */}
          <div
            className="talk-modal-content"
            ref={contentRef}
            onScroll={handleScroll}
            style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px' }}
          >
            {/* Show loading state only if no results AND loading */}
            {isLoading && !hasResults ? (
              <div className="talk-modal-empty" style={{ textAlign: 'center', padding: '20px', color: '#667781' }}>
                <p>Loading...</p>
              </div>
            ) : !hasResults ? (
              <div className="talk-modal-empty" style={{ textAlign: 'center', padding: '20px', color: '#667781' }}>
                <p>No people or teams found</p>
              </div>
            ) : (
              <>
                {/* Users */}
                {users.length > 0 && (
                  <div className="talk-modal-section">
                    <div className="talk-modal-section-title ui-font-12" style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '600', color: '#667781', textTransform: 'uppercase', marginBottom: '8px', marginTop: '12px' }}>
                      <FiUser size={14} />
                      <span>People</span>
                    </div>
                    {users.map((user) => (
                      <button
                        key={user.id}
                        className="talk-modal-item"
                        onClick={() => handleStartDirectChat(user)}
                        disabled={isCreating}
                        style={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '8px 12px',
                          border: 'none',
                          background: 'transparent',
                          cursor: 'pointer',
                          borderRadius: '8px',
                          transition: 'background 0.2s',
                          textAlign: 'left'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.04)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        <Avatar
                          src={user.avatar}
                          name={user.name}
                          size={40}
                        />
                        <div className="talk-modal-item-info">
                          <div className="talk-modal-item-name ui-font-14" style={{ fontWeight: '600', color: '#111B21' }}>{user.name}</div>
                          {user.email && (
                            <div className="talk-modal-item-subtitle ui-font-12" style={{ color: '#667781' }}>{user.email}</div>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {/* Teams */}
                {teams.length > 0 && (
                  <div className="talk-modal-section">
                    <div className="talk-modal-section-title ui-font-12" style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '600', color: '#667781', textTransform: 'uppercase', marginBottom: '8px', marginTop: '12px' }}>
                      <FiUsers size={14} />
                      <span>Teams</span>
                    </div>
                    {teams.map((team) => (
                      <button
                        key={team.id}
                        className="talk-modal-item"
                        onClick={() => handleStartTeamChat(team)}
                        disabled={isCreating}
                        style={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '8px 12px',
                          border: 'none',
                          background: 'transparent',
                          cursor: 'pointer',
                          borderRadius: '8px',
                          transition: 'background 0.2s',
                          textAlign: 'left'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.04)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        <Avatar
                          src={team.avatar}
                          name={team.name}
                          size={40}
                        />
                        <div className="talk-modal-item-info">
                          <div className="talk-modal-item-name ui-font-14" style={{ fontWeight: '600', color: '#111B21' }}>{team.name}</div>
                          <div className="talk-modal-item-subtitle ui-font-12" style={{ color: '#667781' }}>
                            {team.members_count || 0} members
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {isLoading && hasResults && (
                  <div className="ui-font-12" style={{ textAlign: 'center', padding: '10px', color: '#667781' }}>
                    Loading more...
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {isCreating && (
          <div className="talk-modal-loading" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(255,255,255,0.8)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}>
            <div className="talk-modal-spinner" style={{ width: '24px', height: '24px', border: '3px solid #ccc', borderTopColor: '#0C5097', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
            <p style={{ marginTop: '10px', color: '#0C5097', fontWeight: 600 }}>Creating conversation...</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default NewMessageModal;
